import { useMemo } from 'react';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useAppSelector } from 'store/hooks';
import { useFeatureFlagValue } from 'store/session/hooks';

import { getActiveFile } from '../data/reducer';
import {
  VIEWER_KINDS,
  type ViewerKind,
  resolveViewerKind,
} from '../resolveViewerKind';

/**
 * Which viewer renders the active file. The single source of truth — every
 * "is this a PDF / is this 3D / should the CAD viewer mount" question in the app
 * should be answered from this, not by re-deriving from filename or `hasGeometry`.
 * See `resolveViewerKind` for the precedence rules.
 *
 * This is a hook rather than a selector because the decision depends on a feature
 * flag, and flags are read through `useFeatureFlagValue` (the `getFeatureFlagValue`
 * selector is deprecated outside `connect`ed components and sagas).
 */
export const useActiveViewerKind = (): ViewerKind => {
  const activeFile = useAppSelector(getActiveFile);
  const isNewDxfViewerEnabled = useFeatureFlagValue(
    featureFlagKeys.isNewDxfViewerEnabled,
  );

  return useMemo(
    () =>
      resolveViewerKind(activeFile, {
        isNewDxfViewerEnabled: Boolean(isNewDxfViewerEnabled),
      }),
    [activeFile, isNewDxfViewerEnabled],
  );
};

/** True when the PDF viewer is the active viewer for this file. */
export const useIsActiveFilePDFCompatible = (): boolean =>
  useActiveViewerKind().kind === VIEWER_KINDS.PDF;

/**
 * True when the CAD viewer is the active viewer — i.e. gates the CAD toolbars,
 * measurement displays, and interrogation panels.
 *
 * NB: this is false for DXF/DWG files rendered by the new DXF scene viewer
 * (flag-gated), which is intended — those files must not show CAD tooling. Legacy
 * DXFs still resolve to MODEL and behave exactly as before.
 */
export const useIsActiveFile3DRenderable = (): boolean =>
  useActiveViewerKind().kind === VIEWER_KINDS.MODEL;
